// Humanised Activity Stream — reads /api/admin/activity and renders a
// readable timeline of meaningful walker actions. Replaces the raw
// "Recent events" table as the primary admin feed; the raw table stays
// behind a "Show raw events" toggle for incident forensics.
//
// Sentences are built on the FRONTEND from `type + meta` so the backend
// stays structured and the copy can evolve without data migrations.

import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  UserPlus,
  PawPrint,
  ShieldAlert,
  Camera,
  Target,
  Trophy,
  Share2,
  Loader2,
  ChevronDown,
} from "lucide-react";
import { api } from "../lib/api";

const ICON_BY_TYPE = {
  signup: UserPlus,
  review: PawPrint,
  hazard: ShieldAlert,
  photo: Camera,
  scout_verified: Target,
  badge: Trophy,
  share: Share2,
};

const BADGE_LABEL = {
  trail_scout: "The Trail Scout",
  master_scout: "The Master Scout",
};

/** Build the human sentence for a feed row on the frontend. Backend only
 *  returns structured data; the copy evolves here without migrations. */
function buildSentence(item) {
  const actor = item.actor_first_name || "A walker";
  const trail = item.trail_name || null;
  const trailId = item.trail_id || null;
  const meta = item.meta || {};
  const trailEl = trail && trailId ? (
    <Link to={`/location/${trailId}`} className="text-brand-primary hover:underline">
      {trail}
    </Link>
  ) : (
    <span className="text-brand-text">this trail</span>
  );
  switch (item.type) {
    case "signup":
      return <>{actor} joined the pack.</>;
    case "review": {
      const rating = Number.isFinite(meta.rating) ? meta.rating : null;
      // Rating is `dog_friendliness` only — phrase it accordingly so
      // admins don't misread it as an overall star rating.
      if (rating != null && trail) {
        return <>{actor} rated {trailEl} {rating}★ for dog-friendliness.</>;
      }
      if (trail) return <>{actor} reviewed {trailEl}.</>;
      return <>{actor} posted a review.</>;
    }
    case "hazard": {
      const hazard = meta.hazard_type || "an issue";
      const suffix = meta.resolved ? " — now resolved" : "";
      if (trail) return <>A hazard was reported on {trailEl}: {hazard}{suffix}.</>;
      return <>A hazard was reported: {hazard}{suffix}.</>;
    }
    case "photo":
      if (trail) return <>{actor} uploaded a photo of {trailEl}.</>;
      return <>{actor} uploaded a photo.</>;
    case "scout_verified":
      if (trail) return <>{actor} verified {trailEl} as a Scout Needed trail.</>;
      return <>{actor} verified a Scout Needed trail.</>;
    case "badge": {
      const label = BADGE_LABEL[meta.badge_key] || "a scout rune";
      return <>{actor} earned {label} rune.</>;
    }
    case "share": {
      const channel = meta.share_channel ? ` via ${meta.share_channel}` : "";
      if (trail) return <>{actor} shared {trailEl}{channel}.</>;
      return <>{actor} shared a trail{channel}.</>;
    }
    default:
      return <>{actor} did something on TrailDog.</>;
  }
}

function relativeTime(iso) {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
}

/** Group rows into Today / Yesterday / Earlier buckets based on local
 *  midnight boundaries. Items keep their original order inside buckets. */
function groupByBucket(items) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 24 * 3600_000;
  const groups = { today: [], yesterday: [], earlier: [] };
  for (const it of items) {
    const t = new Date(it.occurred_at).getTime();
    if (!Number.isFinite(t)) {
      groups.earlier.push(it);
    } else if (t >= startOfToday) {
      groups.today.push(it);
    } else if (t >= startOfYesterday) {
      groups.yesterday.push(it);
    } else {
      groups.earlier.push(it);
    }
  }
  return groups;
}

export default function ActivityStream() {
  // `pages` holds every row we've accumulated so far via Load more.
  const [pages, setPages] = useState([]);
  const [cursor, setCursor] = useState(null); // null = first page

  const q = useQuery({
    queryKey: ["admin-activity", cursor],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "50" });
      if (cursor) params.set("before", cursor);
      const res = await api.get(`/admin/activity?${params}`);
      return res.data;
    },
    // Keep stale time short so refreshing the dashboard surfaces fresh
    // actions quickly without re-firing on every hover.
    staleTime: 15_000,
  });

  // TanStack Query v5 removed the useQuery `onSuccess` callback, so we
  // accumulate pages in an effect keyed on the fetched data instead.
  // The de-dup set prevents accidental duplicate rows if a refetch
  // overlaps the next page.
  useEffect(() => {
    const incoming = q.data?.items;
    if (!incoming || incoming.length === 0) return;
    setPages((prev) => {
      const seen = new Set(prev.map((p) => p.id));
      const fresh = incoming.filter((i) => !seen.has(i.id));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  }, [q.data]);

  // Initial fetch result also feeds into pages via onSuccess, but we show
  // a loader until at least one page lands.
  const items = pages;
  const groups = groupByBucket(items);
  const nextBefore = q.data?.next_before || null;

  return (
    <section
      className="td-card p-5 sm:p-6"
      data-testid="admin-activity-stream"
    >
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-medium">Activity stream</h2>
        <span className="text-xs text-brand-subtle">
          {items.length ? `${items.length} shown` : null}
        </span>
      </div>

      {q.isLoading && items.length === 0 ? (
        <div className="mt-4 flex items-center gap-2 text-brand-subtle text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading activity…
        </div>
      ) : items.length === 0 ? (
        <p
          data-testid="activity-empty"
          className="mt-4 text-sm text-brand-subtle italic"
        >
          Quiet on the trails — no new activity yet.
        </p>
      ) : (
        <div className="mt-4 space-y-6">
          {["today", "yesterday", "earlier"].map((bucket) => {
            const rows = groups[bucket];
            if (!rows.length) return null;
            const title = bucket === "today" ? "Today" : bucket === "yesterday" ? "Yesterday" : "Earlier";
            return (
              <div key={bucket} data-testid={`activity-group-${bucket}`}>
                <div className="td-section-label !mt-0 mb-3">{title}</div>
                <ul className="space-y-3">
                  {rows.map((it) => {
                    const Icon = ICON_BY_TYPE[it.type] || PawPrint;
                    return (
                      <li
                        key={it.id}
                        data-testid={`activity-row-${it.type}`}
                        className="flex items-start gap-3"
                      >
                        <div
                          className="w-8 h-8 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0"
                          aria-hidden="true"
                        >
                          <Icon size={15} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-brand-text leading-snug">
                            {buildSentence(it)}
                          </div>
                          <div
                            className="text-[11px] text-brand-subtle mt-0.5"
                            title={new Date(it.occurred_at).toLocaleString("en-GB")}
                          >
                            {relativeTime(it.occurred_at)}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {nextBefore && (
        <button
          type="button"
          onClick={() => setCursor(nextBefore)}
          disabled={q.isFetching}
          data-testid="activity-load-more"
          className="td-btn-secondary mt-5 w-full !py-2 !px-4 !text-sm whitespace-nowrap"
        >
          {q.isFetching ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <ChevronDown size={14} />
          )}
          Load more
        </button>
      )}
    </section>
  );
}
